import { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Divider,
  Grid,
  Avatar,
  Button,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import apiService from "../../../service/Api";
import ConvertToBookingDialog from "./QuoteConverttoBookingDialog";
import { tokens } from "../../../theme";

interface QuoteResponse {
  id: number;
  request_type: string;
  contact?: {
    id: number;
    first_name: string;
    last_name: string;
    email?: string | null;
    phone?: string | null;
  };

  event: {
    id: number;
    title: string;

  };
  deceased?: {
    first_name: string;
    surname: string;
    nickname?: string;
    date_of_birth?: string;
    date_of_death?: string;
    person_image?: string;
  } | null;
  event_datetime: string;
  venue_name: string;
  venue_latitude: string;
  venue_longitude: string;
  created_at: string;
}

const QuoteRequestView = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const [convertOpen, setConvertOpen] = useState(false);

  const handleOpenConvertDialog = () => {
    setConvertOpen(true);
  };

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await apiService.get<QuoteResponse>(
          `/api/bookings/quote-request/${id}/`,
        );
        setQuote(response.data);
      } catch (err) {
        console.error("Failed to fetch quote", err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [id]);

  if (loading) return <Typography>Loading...</Typography>;
  if (!quote) return <Typography>No quote found.</Typography>;

  // Map backend fields for display
  const eventDate = new Date(quote.event_datetime).toLocaleDateString();
  const eventTime = new Date(quote.event_datetime).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const belovedName = quote.deceased
    ? `${quote.deceased.first_name} ${quote.deceased.surname}`
    : "N/A";

  const clientName = quote.contact
    ? `${quote.contact.first_name} ${quote.contact.last_name}`
    : "N/A";
  const clientPhone = quote.contact?.phone || "N/A";
  const clientEmail = quote.contact?.email || "N/A";

  const formatEventDateWithDay = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const weekday = date.toLocaleDateString("en-US", {
      weekday: "long",
    });
    const originalFormat = date.toLocaleDateString("en-US");
    return `${weekday}, ${originalFormat}`;
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 1 }}>
        <Button
          sx={{ border: 2, borderColor: colors.primary[700], color: colors.primary[700] }}
          onClick={handleOpenConvertDialog}
        >
          Convert to Booking
        </Button>
        <Button sx={{ border: 2, borderColor: colors.primary[700], color: colors.primary[700] }}>
          Generate Memorial Pages
        </Button>

        <Button
          onClick={() => {
            navigate("/proposals/proposals-builder", {
              state: {
                fromQuoteRequest: true,
                quoteId: quote.id,
                clientId: quote?.contact?.id,
                deceased: quote.deceased
                  ? {
                    first_name: quote.deceased.first_name,
                    surname: quote.deceased.surname,
                    date_of_birth: quote.deceased.date_of_birth,
                    date_of_death: quote.deceased.date_of_death,
                  }
                  : null,
                event: {
                  datetime: quote.event_datetime,
                  venue_name: quote.venue_name,
                },
              },
            });
          }}
          sx={{ border: 2, borderColor: colors.primary[700], color: colors.primary[700] }}
        >
          Create Proposal
        </Button>
      </Box>

      <Card sx={{ borderRadius: 3, overflow: "hidden", boxShadow: 0 }}>
        <Box sx={{ background: "#614338", color: "#fff", p: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Quote Request Details
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
            Submitted on {new Date(quote.created_at).toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            ID: {quote.id}
          </Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Event Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography>
                <strong>Event Date:</strong>{" "}
                {formatEventDateWithDay(quote.event_datetime)}
              </Typography>

              <Typography>
                <strong>Start Time:</strong> {eventTime}
              </Typography>
              <Typography>
                <strong>Location:</strong> {quote.venue_name}
              </Typography>
              <Typography>
                <strong>Venue Coordinates:</strong> {quote.venue_latitude},{" "}
                {quote.venue_longitude}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Personalization Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography sx={{ mb: 2 }}>
                <strong>{quote.request_type === "SPONSORSHIP" ? "Event Title" : "Beloved Name:"}:</strong> {quote.request_type === "SPONSORSHIP" ? quote.event.title : quote.deceased?.first_name}
              </Typography>
              {quote.request_type === "SPONSORSHIP" && (
                <Box sx={{ width: "100%", height: "100%" }}>
                  <Avatar
                    src="https://i.pinimg.com/736x/d4/f9/70/d4f970750aadd73c1ab1fdee51fc46c1.jpg"
                    alt={belovedName}
                    variant="rounded"
                    sx={{
                      width: 120,
                      height: 100,
                    }}
                  >
                    {quote.event?.title?.[0]}
                  </Avatar>
                </Box>
              )}
              {quote.deceased && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    src={quote.deceased.person_image || "https://i.pinimg.com/736x/d4/f9/70/d4f970750aadd73c1ab1fdee51fc46c1.jpg"}
                    alt={belovedName}
                    sx={{ width: 64, height: 64 }}

                  >   {quote.request_type === "SPONSORSHIP" ? quote.event.title[0] : quote.deceased.first_name[0]} </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Full Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {belovedName}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Client Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography>
                <strong>Client Name:</strong> {clientName}
              </Typography>
              <Typography>
                <strong>Phone:</strong> {clientPhone}
              </Typography>
              <Typography>
                <strong>Email:</strong> {clientEmail}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, bgcolor: "#f9fafb", borderRadius: 2, p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Complete Information Summary
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Request ID:</strong>
                </Typography>
                <Typography>{quote.id}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Submitted:</strong>
                </Typography>
                <Typography>
                  {new Date(quote.created_at).toLocaleString()}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Event Date:</strong>
                </Typography>
                <Typography>{eventDate}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Event Time:</strong>
                </Typography>
                <Typography>{eventTime}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Location:</strong>
                </Typography>
                <Typography>{quote.venue_name}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Coordinates:</strong>
                </Typography>
                <Typography>
                  {quote.venue_latitude}, {quote.venue_longitude}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      <ConvertToBookingDialog
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        quoteId={quote.id}
      />
    </Box>
  );
};

export default QuoteRequestView;
